import React from "react";
import { Car, Bike, Truck, Cog, LayoutGrid, type LucideIcon } from "lucide-react";
import { cn } from "../../lib/utils";

export const VEHICLE_CATEGORIES: { key: string; label: string; sub: string; icon: LucideIcon }[] = [
  { key: "passenger", label: "Passenger", sub: "Car, SUV, MPV, Van", icon: Car },
  { key: "motorcycle", label: "Motorcycle", sub: "Bike, Scooter", icon: Bike },
  { key: "heavy", label: "Heavy", sub: "Lorry, Bus, Trailers", icon: Truck },
  { key: "others", label: "Others", sub: "Special vehicles", icon: Cog },
];

interface Props {
  value: string;
  onChange: (key: string) => void;
  allowAll?: boolean;
  compact?: boolean;
  testIdPrefix?: string;
  className?: string;
}

export function CategoryPicker({ value, onChange, allowAll = false, compact = false, testIdPrefix = "category-tile", className }: Props) {
  const items = allowAll ? [{ key: "", label: "All", sub: "Every category", icon: LayoutGrid }, ...VEHICLE_CATEGORIES] : VEHICLE_CATEGORIES;
  return (
    <div role="radiogroup" data-testid={`${testIdPrefix}-group`} className={cn(compact ? "flex flex-wrap gap-1.5" : "grid grid-cols-2 gap-2.5 sm:grid-cols-4", className)}>
      {items.map((c) => {
        const active = value === c.key;
        return (
          <button
            key={c.key || "all"} type="button" role="radio" aria-checked={active} data-testid={`${testIdPrefix}-${c.key || "all"}`} onClick={() => onChange(c.key)}
            className={cn(
              "group border-2 bg-white text-left transition-[border-color,background-color,transform] duration-150 hover:-translate-y-px focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--brand)]/40",
              compact ? "flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold" : "flex flex-col items-center rounded-xl px-3 py-4 text-center",
              active ? "border-[color:var(--brand)] bg-indigo-50/70 text-[color:var(--brand)]" : "border-slate-200 text-slate-700 hover:border-slate-300"
            )}
          >
            <c.icon className={cn(compact ? "h-3.5 w-3.5" : "mb-2 h-7 w-7", active ? "text-[color:var(--brand)]" : "text-slate-500 group-hover:text-slate-700")} strokeWidth={1.75} />
            <span className={cn(compact ? "" : "font-display text-sm font-bold", active ? "text-[color:var(--brand)]" : "text-slate-800")}>{c.label}</span>
            {!compact && <span className={cn("mt-0.5 text-[11px]", active ? "text-[color:var(--brand)]/80" : "text-slate-500")}>{c.sub}</span>}
          </button>
        );
      })}
    </div>
  );
}
